import {memo, useRef} from 'react';

import {sanitizeDigits} from '../../lib/sanitize-digits';
import {Typography} from '../Typography';
import {OtpCodeBox} from './OtpCodeBox';

import type {ChangeEvent, ClipboardEvent, KeyboardEvent, ReactNode} from 'react';

export interface OtpCodeFieldProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  label?: ReactNode;
  error?: boolean;
  disabled?: boolean;
}

const DEFAULT_LENGTH = 6;

/** One-digit-per-box code input for step-up (MFA) verification codes; boxes always read left-to-right. */
export const OtpCodeField = memo(function OtpCodeField({
  value,
  onChange,
  length = DEFAULT_LENGTH,
  label,
  error,
  disabled
}: OtpCodeFieldProps) {
  const boxRefs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({length}, (_, i) => value[i] ?? '');

  const setDigits = (next: string[]) => onChange(next.join('').slice(0, length));

  const handleChange = (index: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const digit = sanitizeDigits(e.target.value, 1);
    const next = digits.slice();
    next[index] = digit;
    setDigits(next);
    if (digit && index < length - 1) boxRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      const next = digits.slice();
      next[index - 1] = '';
      setDigits(next);
      boxRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index < length - 1) {
      boxRefs.current[index + 1]?.focus();
    } else if (e.key === 'ArrowRight' && index > 0) {
      boxRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const raw = sanitizeDigits(e.clipboardData.getData('text'), length);
    if (!raw) return;
    e.preventDefault();
    setDigits(raw.split(''));
    boxRefs.current[Math.min(raw.length, length - 1)]?.focus();
  };

  return (
    <div className="flex w-full flex-col items-start gap-1.5">
      {label && (
        <Typography size="sm" weight="semibold">
          {label}
        </Typography>
      )}
      <div className="flex w-full items-start justify-center gap-2.5" dir="ltr">
        {digits.map((digit, index) => (
          <OtpCodeBox
            key={index}
            inputRef={el => {
              boxRefs.current[index] = el;
            }}
            value={digit}
            onChange={handleChange(index)}
            onKeyDown={handleKeyDown(index)}
            onPaste={handlePaste}
            error={error}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
});
